import _ from 'underscore';

import {toCamelCase, toPascalCase, toSnakeCase} from 'common/utils';

/**
 *
 * @param {BaseApi} api - the api integration to use, e.g. ArchitectApi
 * @param {string} actionName - the api action to trigger, e.g. `fetchSharedFeeds`
 * @param {Object} params - any additonal params needed for action to be called
 * @returns {Promise} for paginated results
 */
export function fetchItemsByCursor(api, actionName, params) {
  // fetchPage returns a list of Promises from the API
  const fetchPage = (cursor = null, result = []) =>
    api[actionName](params, cursor)
      .then(response => {
        const newCursor = response.headers.get('x-cursor');
        const newResult = result.concat(response.json());
        if (newCursor === null) return newResult;
        return fetchPage(newCursor, newResult);
      });

  // resolved value is an array of promises b/c response.json() returns a promise
  return fetchPage();
}

// Call this inside of await to add delay in script
// Created to add interval in consecutive API calls
// Eg. Fetching rides
function delay(timeInMS) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, timeInMS);
  });
}


/**
 *
 * @param {BaseApi} api - the api integration to use, e.g. ArchitectApi
 * @param {string} actionName - the api action to trigger, e.g. `fetchSharedFeeds`
 * @param {Object} params - any additonal params needed for action to be called
 * @param {string} cursor - cursor of last request made
 * @returns {Promise} for paginated results
 */
export function fetchItemsByCursorAndDispatch(api, actionName, params, dispatchData) {
  // fetchPage returns a list of Promises from the API until cursor is not null
  return new Promise((resolve) => {
    const fetchPage = (cursor = null, result = []) =>
    api[actionName](params, cursor)
      .then(async (response) => {
        const newCursor = response.headers.get('x-cursor');
        const newResult = result.concat(await response.json());

        let results = await Promise.all(newResult);
        results = [].concat(...results);
        results = dispatchData.deserializer(results).map((results) => {
          return new dispatchData.DataType({...params, ...results});
        });
        results = _.indexBy(results, dispatchData.idProp);
        if (newCursor === null) await delay(3000); // Added 3 seconds graceful delay
        await dispatchData.dispatch(dispatchData
        .actions[`receive${dispatchData.pluralActionName}v2`]([results,
          newCursor], ...dispatchData.args));
        if (newCursor === null) return resolve('all done');
        return fetchPage(newCursor, newResult);
      });

  // resolved value is an array of promises b/c response.json() returns a promise
    return fetchPage();
  });
}

/**
 * Async helper function to pull in all items served by a paginated
 * API.
 *
 * @param {BaseApi} api - the BaseApi subclass to use for API calls. **NOTE** that
 * this must have a method named `actionName` with the
 * signature `(index: number, limit: number | null, params: object) => Promise<object>`.
 * @param {string} actionName - the name of the method to call on `api`
 * @param {Object} params - params to pass to the API call
 */
async function fetchAllItemsByPage(
  api,
  actionName,
  params,
) {
  // total starts as null and will be set after first result
  let resultTotal = null;
  let nextIndex = 0;
  let resultItems = [];

  // perform at least one fetch (the initial fetch determined by
  // `total` being null); perform subsequent fetches until the
  // set of items is as large as limit, if it exists, or total
  // if it does not
  while (resultItems.length !== resultTotal) {
    const response = await api[actionName](nextIndex, null, params);

    switch (response.status) {
      case 200: {
        const {index, items, total} = await response.json();

        resultTotal = total;
        nextIndex = index + 1;
        resultItems = resultItems.concat(items);
        break;
      }
      default: {
        return {
          items: [],
          total: 0,
          index: 0,
          limit: 0,
        };
      }
    }
  }

  return {
    items: resultItems,
    total: resultTotal,
    index: 0,
    limit: 0,
  };
}
function shouldFetchItems(state, params) {
  if (!state) {
    return true;
  } else if (_.isEmpty(params) && state.isFetching) {
    return false;
  } else if (_.isEmpty(params) && _.isEmpty(state.items)) {
    return true;
  }
  // return true if any param does not match what is in the store
  return _.some(_.map(params, (value, key) => state[key] !== value));
}

// Returns true if limit is defined, greater than 0, and does not equal last limit used.
function limitHasChanged(state, limit) {
  return limit && state.limit !== limit;
}

export const _shouldFetchItems = shouldFetchItems; // testing

// Create action creators for actions that fetch data from a remote source.
// "keys" is an array of properties that uniquely identifies the fetched data.
// For example, a list of routes might be keyed on ['agencyId'] while a list of
// trips might be keyed on ['agencyId', 'feedId', 'routeId']
export function createFetchListActions({
  name,
  pluralName = `${name}s`,
  DataType,
  idProp = `${toSnakeCase(name)}_id`,
  keys = [],
  api,
  deserializer = ((items) => items),
}) {
  const actionName = toPascalCase(name);
  const pluralActionName = toPascalCase(pluralName);
  const fetchPluralAction = `fetch${pluralActionName}`;
  const reducerName = toSnakeCase(name).toUpperCase();
  const pluralReducerName = toSnakeCase(pluralName).toUpperCase();
  const stateName = toCamelCase(name);
  let fetchPromise = Promise.resolve();

  const actions = {};

  actions[`clear${pluralActionName}`] = () => ({
    type: `CLEAR_${pluralReducerName}`,
  });

  actions[`request${pluralActionName}`] = (...args) => ({
    type: `REQUEST_${pluralReducerName}`,
    ...(_.object(keys, args)),
  });

  actions[`receive${pluralActionName}`] = (json, ...args) => ({
    type: `RECEIVE_${pluralReducerName}`,
    items: json,
    ...(_.object(keys, args)),
  });

  actions[`receive${pluralActionName}v2`] = (results, ...args) => ({
    type: `RECEIVE_${pluralReducerName}`,
    items: results[0],
    cursor: results[1],
    hasMore: !!results[1],
    ...(_.object(keys, args)),
  });

  actions[`receiveSingle${actionName}`] = (id, item, ...args) => ({
    type: `RECEIVE_SINGLE_${reducerName}`,
    id,
    item,
    ...(_.object(keys, args)),
  });

  actions[fetchPluralAction] = (...args) =>
    (dispatch) => {
      dispatch(actions[`request${pluralActionName}`](...args));
      const params = _.object(keys, args);
      return fetchItemsByCursor(api, fetchPluralAction, params)
        .then(promises =>
          // concatenate all the fetchPage results into a single list
          Promise.all(promises).then((results) => [].concat(...results))
        )
        .then(items => deserializer(items).map((item) => new DataType({...params, ...item})))
        .then(items => _.indexBy(items, idProp))
        .then(obj => dispatch(actions[`receive${pluralActionName}`](obj, ...args)));
    };

  actions[`${fetchPluralAction}v2Action`] = (...args) =>
    async (dispatch) => {
      dispatch(actions[`request${pluralActionName}`](...args));
      const params = _.object(keys, args);
      // We offload the dispatch to the `fetchItemsByCursorAndDispatch` method
      await fetchItemsByCursorAndDispatch(api, fetchPluralAction, params, {
        dispatch,
        actions,
        pluralActionName,
        args,
        deserializer,
        DataType,
        idProp
      });
    };

  actions[`${fetchPluralAction}v2`] = (...args) =>
    (dispatch, getState) => {
      const params = _.object(keys, args);
      if (shouldFetchItems(getState()[stateName], params)) {
        fetchPromise = dispatch(actions[`${fetchPluralAction}v2Action`](...args));
        return fetchPromise;
      }
      return fetchPromise;
    };

  actions[`${fetchPluralAction}IfNeeded`] = (...args) =>
    (dispatch, getState) => {
      const params = _.object(keys, args);
      if (shouldFetchItems(getState()[stateName], params)) {
        fetchPromise = dispatch(actions[fetchPluralAction](...args));
        return fetchPromise;
      }
      return fetchPromise;
    };

  actions[`fetch${actionName}`] = (id, ...args) =>
    (dispatch) => {
      dispatch(actions[`request${pluralActionName}`](...args));
      const params = _.object(keys, args);
      return api[`fetch${actionName}`](id, params)
        .then(response => response.json())
          .then(item => new DataType({...params, ...item}))
          .then(item => dispatch(actions[`receiveSingle${actionName}`](id, item, ...args)));
    };

  actions[`requestRemove${actionName}`] = () => ({
    type: `REQUEST_REMOVE_${reducerName}`,
  });

  actions[`receiveRemove${actionName}`] = (id) => ({
    type: `RECEIVE_REMOVE_${reducerName}`,
    id,
  });

  actions[`delete${actionName}`] = (id, ...args) =>
    (dispatch) => {
      dispatch(actions[`requestRemove${actionName}`]());

      return api[`delete${actionName}`](id, ...args)
        .then((response) => {
          switch (response.status) {
            case 200:
              dispatch(actions[`receiveRemove${actionName}`](id));
              return response.text();

            case 400:
              dispatch(actions[`receiveRemove${actionName}`](null));
              return response.json()
                .then(json => {
                  // TODO: Pass this error through so it can be presented on the front-end
                  alert(json.description); // eslint-disable-line no-alert
                });

            case 403:
              dispatch(actions[`receiveRemove${actionName}`](null));
              // eslint-disable-next-line no-alert
              alert('Your user account is not authorized to perform this action.');
              return response.json();

            case 503:
              dispatch(actions[`receiveRemove${actionName}`](null));
              return response.json()
                .then(json => {
                  // TODO: Pass this error through so it can be presented on the front-end
                  alert(json.description); // eslint-disable-line no-alert
                });

            default:

              dispatch(actions[`receiveRemove${actionName}`](null));
              throw response;
          }
        });
    };
  return actions;
}

export function createPaginatedItemActions({
  name,
  pluralName = `${name}s`,
  DataType,
  keys = [],
  api,
  deserializer = ((items) => items),
  defaultLimit,
}) {
  const pluralActionName = toPascalCase(pluralName);
  const pluralReducerName = toSnakeCase(pluralName).toUpperCase();
  const stateName = toCamelCase(name);
  let promised = Promise.resolve();

  const actions = {};

  actions[`clear${pluralActionName}`] = () => ({
    type: `CLEAR_${pluralReducerName}`,
  });

  actions[`requestPageOf${pluralActionName}`] = (index, ...args) => ({
    type: `REQUEST_PAGE_OF_${pluralReducerName}`,
    index,
    ...(_.object(keys, args)),
  });

  actions[`receivePageOf${pluralActionName}`] = (
    {
      index,
      items,
      limit,
      total,
    },
    append = false,
    ...args
  ) => ({
    type: `RECEIVE_PAGE_OF_${pluralReducerName}`,
    index,
    items,
    limit,
    total,
    append,
    ...(_.object(keys, args)),
  });

  actions[`requestAll${pluralActionName}`] = (...args) => ({
    type: `REQUEST_ALL_${pluralReducerName}`,
    ...(_.object(keys, args)),
  });

  actions[`receiveAll${pluralActionName}`] = ({items, total}, ...args) => ({
    type: `RECEIVE_ALL_${pluralReducerName}`,
    items,
    total,
    ...(_.object(keys, args)),
  });

  /**
   * Requests a page of the configured resource.
   * API method should expect request parameters, page index, and page limit.
   * If excludeLimit is true, the limit will not be provided to the API.
   *
   * This expects the result of the API call to be a JSON object as follows:
   *
   * {
   *   items: [ <resources> ],
   *   total: <the total number of items across all pages>,
   *   limit: <the actual page limit; could be less than requested if maximum is reached>,
   *   index: <the actual page index; could be different than requested>,
   * }
   *
   * @param {number} index - the page index to be requested (0, ...)
   * @param {number} limit - the size of the page to be requested; if limit is 0, excluded
   * @param {boolean} append - if true, add results to state; if false, replace state with results
   * @param {*} args - the remaining arguments provided to method; passed to API request
   * @returns {Promise} - promises the resulting page of items
   */
  actions[`fetchPageOf${pluralActionName}`] = (index, limit, append = false, ...args) =>
    (dispatch) => {
      // intentionally not including index and limit until request is successful
      dispatch(actions[`requestPageOf${pluralActionName}`](index, ...args));
      const params = _.object(keys, args);
      return api[`fetchPageOf${pluralActionName}`](
        index,
        limit === 0 ? null : (limit || defaultLimit),
        params,
      )
        .then(response => response.json())
        .then(response => ({
          ...response,
          items: deserializer(response.items).map((item) => new DataType({...params, ...item})),
        }))
        .then(obj => dispatch(actions[`receivePageOf${pluralActionName}`](obj, append, ...args)));
    };

  actions[`fetchPageOf${pluralActionName}IfNeeded`] = (index, limit, append = false, ...args) =>
    (dispatch, getState) => {
      const state = getState()[stateName];
      const params = {index, ..._.object(keys, args)};
      if (shouldFetchItems(state, params) || limitHasChanged(state, limit)) {
        promised = dispatch(actions[`fetchPageOf${pluralActionName}`](
          index,
          limit,
          append,
          ...args
        ));
      }
      return promised;
    };

  actions[`fetchAll${pluralActionName}`] = (...args) =>
    (dispatch) => {
      dispatch(actions[`requestAll${pluralActionName}`](...args));
      const params = _.object(keys, args);
      return fetchAllItemsByPage(api, `fetchPageOf${pluralActionName}`, params)
      .then(response => ({
        ...response,
        items: deserializer(response.items).map((item) => new DataType({...params, ...item})),
      }))
      .then(obj => dispatch(actions[`receiveAll${pluralActionName}`](obj, ...args)));
    };

  actions[`fetchAll${pluralActionName}IfNeeded`] = (...args) =>
    (dispatch, getState) => {
      const params = _.object(keys, args);
      if (shouldFetchItems(getState()[stateName], params)) {
        promised = dispatch(actions[`fetchAll${pluralActionName}`](...args));
      }

      return promised;
    };

  return actions;
}

export function createDraftActions({
  name,
  DataType,
  keys = [],
  api,
}) {
  const actions = {};

  const actionName = toPascalCase(name);
  const reducerName = toSnakeCase(name).toUpperCase();
  const overrideApi = api;

  actions[`new${actionName}Draft`] = () => ({
    type: `NEW_${reducerName}_DRAFT`,
  });

  actions[`set${actionName}Draft`] = (item) => ({
    type: `SET_${reducerName}_DRAFT`,
    item,
  });

  actions[`edit${actionName}Draft`] = (key, value) => ({
    type: `EDIT_${reducerName}_DRAFT`,
    key,
    value,
  });

  actions[`save${actionName}Draft`] = () => ({
    type: `SAVE_${reducerName}_DRAFT`,
  });

  actions[`undo${actionName}Draft`] = () => ({
    type: `UNDO_${reducerName}_DRAFT`,
  });

  actions[`redo${actionName}Draft`] = () => ({
    type: `REDO_${reducerName}_DRAFT`,
  });

  actions[`clear${actionName}Draft`] = () => ({
    type: `CLEAR_${reducerName}_DRAFT`,
  });

  actions[`receive${actionName}DraftErrors`] = (errors) => ({
    type: `RECEIVE_${reducerName}_DRAFT_ERRORS`,
    errors,
  });

  actions[`receive${actionName}`] = (item) => ({
    type: `RECEIVE_${reducerName}`,
    item,
  });

  actions[`receive${actionName}Draft`] = (item) => ({
    type: `RECEIVE_${reducerName}_DRAFT`,
    item,
  });

  actions[`request${actionName}Details`] = () => ({
    type: `REQUEST_${reducerName}_DETAILS`,
  });

  /**
   * Generates an action that fetches and transforms a single resource.
   * Accepts the string name of the action to perform on success.
   *
   * @param {string} onSuccessAction - the action to dispatch when the resource is received
   * @returns {Function} - action to fetch and handle a single resource
   */
  const fetchResourceAction = (onSuccessAction) =>
    /**
     * @param {string} id - id of the resource to retrieve
     * @param {*} args - addt'l arguments required to retrieve resource (i.e. agency, feed, etc)
     * @param {Function} - redux action handler
     */
    (id, ...args) => (dispatch, getState, {api}) => {
      dispatch(actions[`request${actionName}Details`]());
      const params = _.object(keys, args);
      return (overrideApi || api)[`fetch${actionName}`](id, params)
        .then(response => {
          switch (response.status) {
            case 200:
              return response.json()
                .then(item => new DataType({...params, ...item}))
                .then(item => {
                  return dispatch(actions[onSuccessAction](item));
                });
            case 403:
              dispatch(actions[`receive${actionName}DraftErrors`]([]));
              // eslint-disable-next-line no-alert
              alert('Your user account is not authorized to perform this action.');
              throw response;
            default:
              throw response;
          }
        });
    };

  /**
   * Directly fetches a resource and sets it as the draft for the resource
   */
  actions[`fetch${actionName}AsDraft`] = fetchResourceAction(`receive${actionName}Draft`);

  /**
   * Directly fetches a resource and stores it in the set of items (be it an array or object)
   *
   * In this case, additional details for a resource are required for the resource
   * edit form that were NOT provided by the listing out of the resource (e.g. pagination).
   *
   * For example in Architect, fetching a list of patterns returns a simplified set of data
   * but the full details are needed when navigating to the pattern edit page.
   */
  actions[`fetch${actionName}Details`] = fetchResourceAction(`receive${actionName}`);

  actions[`send${actionName}Draft`] = (draft, params) =>
    (dispatch, getState, {api}) => {
      dispatch(actions[`save${actionName}Draft`]());
      return (overrideApi || api)[`save${actionName}`](draft.serialize(), params)
        .then(response => {
          switch (response.status) {
            case 200:
              return response.json()
                .then(json => {
                  const item = new DataType(json);
                  dispatch(actions[`receive${actionName}DraftErrors`]([]));
                  dispatch(actions[`set${actionName}Draft`](item));
                  return dispatch(actions[`receive${actionName}`](item));
                });
            case 400:
              return response.json()
                .then(json => {
                  return dispatch(actions[`receive${actionName}DraftErrors`](json.errors));
                });
            case 403:
              dispatch(actions[`receive${actionName}DraftErrors`]([]));
              // eslint-disable-next-line no-alert
              alert('Your user account is not authorized to perform this action.');
              return response.json();
            case 409:
              return response.json()
                .then(json => {
                  return dispatch(actions[`receive${actionName}DraftErrors`]([{
                    parameter: actionName,
                    description: json.description,
                  }]));
                });
            default:
              throw response;
          }
        });
    };

  actions[`receive${actionName}OneOffSubmissionErrors`] = (errors) => ({
    type: `RECEIVE_${reducerName}_ONE_OFF_SUBMISSION_ERRORS`,
    errors,
  });

  actions[`submitOneOff${actionName}`] = (draft, params) =>
    (dispatch, getState, {api}) => {
      dispatch(actions[`save${actionName}Draft`]());  // TODO: consider changing name
      return (overrideApi || api)[`save${actionName}`](draft.serialize(), params)
        .then(response => {
          switch (response.status) {
            case 200:
              return response.json()
                .then(json => {
                  const item = new DataType(json);
                  dispatch(actions[`receive${actionName}OneOffSubmissionErrors`]([]));
                  return dispatch(actions[`receive${actionName}`](item));
                });
            case 400:
              return response.json()
                .then(json => {
                  return dispatch(
                    actions[`receive${actionName}OneOffSubmissionErrors`](json.errors));
                });
            case 403:
              // eslint-disable-next-line no-alert
              alert('Your user account is not authorized to perform this action.');
              return dispatch(
                actions[`receive${actionName}OneOffSubmissionErrors`](null));
            case 409:
              return response.json()
                .then(json => {
                  return dispatch(actions[`receive${actionName}OneOffSubmissionErrors`]([{
                    parameter: actionName,
                    description: json.description,
                  }]));
                });
            default:
              throw response;
          }
        });
    };

  return actions;
}

/**
 * Build out actions that fetch data from the api.  In relation to the other action
 * creators, this one makes less decisions about the data and also has some concept
 * of default error handling when fetching.
 *
 * The actions created are Clear, Request, Receive, Fetch.
 *
 * @param {string} name - name to base the actions off of, a name like 'hello world' will produce
 * actions like REQUEST_HELLO_WORLD
 * @param {func} deserializer - Optional function that can specify how the data should be
 * deserialized from the api's json response.
 * @param {func} errorDeserializer - Optional function that can specify how the errors should be
 * deserialized from the api's json response.
 * @param {array of strings} keys - properties used to uniquely identify the fetched data.
 * If the matching utility reducers are used they are used to discard data from out of date
 * requests
 * @returns {actions} - A map of action name:thunk
 */
export function createSimpleFetchActions({
  name,
  deserializer = (data) => data,
  errorDeserializer = (errors) => errors,
  keys = [],
}) {
  const baseActionName = toPascalCase(name);
  const baseReducerName = toSnakeCase(name).toUpperCase();

  const actions = {};

  actions[`clear${baseActionName}`] = () => ({
    type: `CLEAR_${baseReducerName}`,
  });

  actions[`request${baseActionName}`] = (...args) => ({
    type: `REQUEST_${baseReducerName}`,
    ...(_.object(keys, args)),
  });

  actions[`receive${baseActionName}`] = (data, error, ...args) => ({
    type: `RECEIVE_${baseReducerName}`,
    data,
    error,
    ...(_.object(keys, args)),
  });

  actions[`fetch${baseActionName}`] = (...args) =>
    (dispatch, getState, {api}) => {
      dispatch(actions[`request${baseActionName}`](...args));
      return (api)[`fetch${baseActionName}`](...args)
        .then(response => {
          switch (true) {
            case response.status === 200:
              return response.json()
                .then(json => {
                  const data = deserializer(json);
                  return dispatch(actions[`receive${baseActionName}`](data, null, ...args));
                });
            case response.status < 500:
              return response.json()
                .then(json => {
                  const error = errorDeserializer(json);
                  return dispatch(actions[`receive${baseActionName}`](null, error, ...args));
                }).catch(() => { throw response; });
            default:
              throw response;
          }
        });
    };

  return actions;
}



// WEBPACK FOOTER //
// ./src/common/actions/utils.js